/**
 * 13º salário (P1) — por ano: os dois processamentos (1ª e 2ª parcela) como
 * documentos com configuração persistente. Cada cartão: configuração
 * (competência de pagamento, data prevista, observação), tabela de MÉDIAS por
 * pessoa com a origem (override × automática × zerada), cálculo, resultado por
 * pessoa + consolidado, relatório completo e fechamento (imutável). Um
 * processamento por parcela/ano (o ajuste anual é único).
 */
import { Fragment, useEffect, useState } from "react";
import { Badge, Button, Card, Divider, Group, Select, Text, Textarea, Title, Tooltip } from "@mantine/core";
import { IconArrowLeft, IconCalculator, IconCoins, IconLock, IconPrinter } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia, fmtData, fmtMoeda } from "../format";
import { navegar, useQueryRota, origemCompetencia, rotuloVoltarCompetencia } from "../roteador";
import { TabelaDados, Th, TdNum } from "../componentes/listas";
import { BadgeEstadoProcessamento, VisualizadorRelatorio, type RelatorioAberto } from "../componentes/relatorio";
import { CompetenciaField, DataField, MoedaField } from "../componentes/entradas-mantine";
import { Confirmacao, ErroApiAlert, AvisosAlert, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { ReabrirProcessamento } from "../componentes/reabrir-processamento";
import { type EventoResultado } from "./FeriasPage";

interface MediaResolvida {
  funcionarioId: string;
  codigoDominio: number;
  nome: string;
  fonte: string;
  valorInformadoCentavos: number | null;
  overrideCentavos: number | null;
  alerta?: string;
}

interface AvosMes {
  mes: number;
  diasTrabalhados: number;
  contaAvo: boolean;
  motivo: "trabalhado" | "nao_admitido" | "suspenso_inss" | "faltas_injustificadas" | "menos_de_15_dias";
}

interface PessoaResultado {
  codigoDominio: number;
  nome: string;
  situacao: string;
  eventos: EventoResultado[];
  totais: { proventosCentavos: number; descontosCentavos: number; liquidoCentavos: number; fgtsCentavos: number };
  /** M2.5 — memória dos avos mês a mês (runs de 13º). */
  memoria?: { avos13?: { avos: number; meses: AvosMes[] } };
}

interface Detalhe13 {
  id: string;
  tipo: "decimo_primeira" | "decimo_segunda";
  tipoRotulo: string;
  estado: string;
  ano: number;
  competenciaPagamento: number;
  dataPagamento: string | null;
  observacao: string | null;
  runId: string | null;
  mediasOverrides: { funcionarioId: string; valorCentavos: number }[];
  mediasResolvidas: MediaResolvida[];
  avisosMedias: string[];
  runs: { id: string; motorVersion: string; executadoEm: string }[];
  ultimaRun: {
    id: string;
    motorVersion: string;
    executadoEm: string;
    resultado: {
      pessoas: PessoaResultado[];
      totais: {
        proventosCentavos: number;
        descontosCentavos: number;
        liquidoCentavos: number;
        fgtsCentavos: number;
        inssSeguradosCentavos: number;
        irrfCentavos: number;
        encargos: { empresaCentavos: number; terceirosCentavos: number; ratFapCentavos: number };
        headcount: { empregados: number; trabalhando: number; suspensos: number };
      };
    };
  } | null;
}

const ROTULO_FONTE: Record<string, string> = {
  informada: "informada",
  historico: "automática (histórico)",
  zerada: "zerada",
};

/** M2.5 — rótulos da memória de avos mês a mês. */
const ROTULO_MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const ROTULO_MOTIVO: Record<string, string> = {
  trabalhado: "trabalhado",
  nao_admitido: "não admitido",
  suspenso_inss: "suspenso (INSS — B31 paga o período)",
  faltas_injustificadas: "faltas injustificadas",
  menos_de_15_dias: "menos de 15 dias",
};

const ANOS = [2026, 2027, 2028];

export function DecimoPage() {
  // 03/09/2026 (pedido do dono) — deep-link da competência: "?origem=" mostra o
  // botão "Voltar para a competência MM/AAAA" (mesmo padrão das páginas de Férias)
  const origem = origemCompetencia(useQueryRota());
  const [ano, setAno] = useState(2026);
  const [lista, setLista] = useState<{ id: string; tipo: string }[]>([]);
  const [erro, setErro] = useState<unknown>(null);

  async function carregar() {
    const r = await api.get<{ processamentos: { id: string; tipo: string }[] }>(`/processamentos?ano=${ano}`);
    setLista(r.processamentos);
  }
  useEffect(() => {
    carregar().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  const porTipo = (tipo: string) => lista.find((p) => p.tipo === tipo);

  return (
    <>
      {origem && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} />}
          mb="xs"
          data-testid="voltar-competencia"
          onClick={() => navegar(origem)}
        >
          {rotuloVoltarCompetencia(origem)}
        </Button>
      )}
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={2}>13º salário</Title>
          <Text size="sm" c="dimmed">
            Um processamento por parcela e ano, com as médias de variáveis por pessoa persistidas — a 2ª parcela lê o
            adiantamento da 1ª automaticamente. Sócios não participam (contribuintes individuais).
          </Text>
        </div>
        <Select label="Ano de referência" data={ANOS.map(String)} value={String(ano)} onChange={(v) => setAno(Number(v))} w={140} />
      </Group>
      <ErroApiAlert erro={erro} />
      {(["decimo_primeira", "decimo_segunda"] as const).map((tipo) => (
        <CartaoParcela key={`${ano}-${tipo}`} tipo={tipo} ano={ano} existenteId={porTipo(tipo)?.id ?? null} onMudou={carregar} />
      ))}
    </>
  );
}

function CartaoParcela({
  tipo,
  ano,
  existenteId,
  onMudou,
}: {
  tipo: "decimo_primeira" | "decimo_segunda";
  ano: number;
  existenteId: string | null;
  onMudou: () => Promise<void>;
}) {
  const rotulo = tipo === "decimo_primeira" ? "1ª parcela (adiantamento de 50%)" : "2ª parcela (ajuste anual)";
  const [d, setD] = useState<Detalhe13 | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioAberto | null>(null);
  const [confirmar, setConfirmar] = useState<{ titulo: string; consequencia: string; rotulo: string; acao: () => Promise<unknown> } | null>(null);
  // P2 — avisos do último cálculo (ex.: competência de pagamento fechada)
  const [avisosCalc, setAvisosCalc] = useState<string[]>([]);
  // criação
  const [fComp, setFComp] = useState<number | null>(ano * 100 + (tipo === "decimo_primeira" ? 11 : 12));
  const [fData, setFData] = useState<string | null>(null);
  const [fObs, setFObs] = useState("");
  // edição de médias
  const [overrides, setOverrides] = useState<Map<string, number | null>>(new Map());

  useEffect(() => {
    setFComp(ano * 100 + (tipo === "decimo_primeira" ? 11 : 12));
  }, [ano, tipo]);

  async function carregar() {
    if (!existenteId) {
      setD(null);
      return;
    }
    const det = await api.get<Detalhe13>(`/processamentos/${existenteId}`);
    setD(det);
    setOverrides(new Map(det.mediasResolvidas.map((m) => [m.funcionarioId, m.overrideCentavos])));
  }
  useEffect(() => {
    carregar().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existenteId]);

  async function acao(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      notificarOk(mensagem);
      await carregar();
      await onMudou();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setOcupado(false);
      setConfirmar(null);
    }
  }

  const editavel = d !== null && d.estado !== "fechado";

  return (
    <Card withBorder shadow="xs" mb="md">
      <Group justify="space-between" mb="xs" wrap="wrap">
        <Group gap="sm">
          <Title order={4}>{rotulo} — {ano}</Title>
          {d && <BadgeEstadoProcessamento estado={d.estado} />}
        </Group>
        {d && (
          <Group>
            {d.estado !== "fechado" && (
              <Button
                size="xs"
                leftSection={<IconCalculator size={15} />}
                loading={ocupado}
                onClick={() =>
                  d.runs.length > 0
                    ? setConfirmar({
                        titulo: "Recalcular a parcela?",
                        consequencia: "Uma NOVA execução é gravada (a anterior fica no histórico).",
                        rotulo: "Recalcular",
                        acao: async () => {
                          const r = (await api.post(`/processamentos/${d.id}/calcular`)) as { avisos?: string[] };
                          setAvisosCalc(r.avisos ?? []);
                        },
                      })
                    : void acao(async () => {
                        const r = (await api.post(`/processamentos/${d.id}/calcular`)) as { avisos?: string[] };
                        setAvisosCalc(r.avisos ?? []);
                      }, "Parcela calculada.")
                }
              >
                {d.runs.length > 0 ? "Recalcular" : "Calcular"}
              </Button>
            )}
            {d.ultimaRun && (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPrinter size={15} />}
                loading={ocupado}
                onClick={() =>
                  void acao(async () => {
                    const r = (await api.post(`/processamentos/${d.id}/relatorio`)) as { id: string; nomeArquivo: string };
                    setRelatorio({ artefatoId: r.id, nomeArquivo: r.nomeArquivo, titulo: `13º — ${rotulo}` });
                  }, "Relatório pronto.")
                }
              >
                Relatório completo
              </Button>
            )}
            {d.estado === "calculado" && (
              <Button
                size="xs"
                color="teal"
                leftSection={<IconLock size={15} />}
                onClick={() =>
                  setConfirmar({
                    titulo: "Fechar a parcela?",
                    consequencia: "Fechada é imutável: configuração e médias não podem mais ser editadas.",
                    rotulo: "Fechar",
                    acao: () => api.post(`/processamentos/${d.id}/fechar`),
                  })
                }
              >
                Fechar
              </Button>
            )}
            {d.estado === "fechado" && (
              <ReabrirProcessamento
                processamentoId={d.id}
                tipoRotulo="processamento de 13º"
                onReaberto={(avisos) => {
                  setAvisosCalc(avisos);
                  void carregar();
                }}
              />
            )}
          </Group>
        )}
      </Group>
      <ErroApiAlert erro={erro} />
      <AvisosAlert avisos={avisosCalc} />

      {!d ? (
        <>
          <Text size="sm" c="dimmed" mb="sm">
            Ainda não há processamento da {rotulo.toLowerCase()} de {ano}. Crie para configurar as médias e calcular os
            elegíveis do ano.
          </Text>
          <Group align="flex-end" wrap="wrap">
            <CompetenciaField label="Competência de pagamento" valor={fComp} onChange={setFComp} />
            <DataField label="Pagamento previsto (opcional)" valor={fData} onChange={setFData} />
            <Textarea label="Observação (opcional)" value={fObs} onChange={(e) => setFObs(e.currentTarget.value)} minRows={1} />
            <Button
              leftSection={<IconCoins size={16} />}
              disabled={fComp === null}
              loading={ocupado}
              onClick={() =>
                void acao(
                  () =>
                    api.post("/processamentos", {
                      tipo,
                      ano,
                      competenciaPagamento: fComp,
                      dataPagamento: fData,
                      observacao: fObs.trim() || null,
                    }),
                  "Processamento criado — configure as médias e calcule.",
                )
              }
            >
              Criar {rotulo.toLowerCase()}
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            Pagamento em {fmtCompetencia(d.competenciaPagamento)}
            {d.dataPagamento ? ` · previsto ${fmtData(d.dataPagamento)}` : ""}
            {d.observacao ? ` · ${d.observacao}` : ""}
            {d.runs.length > 0 ? ` · ${d.runs.length} execução(ões)` : " · ainda não calculado"}
          </Text>

          <Title order={5} mb={4}>Médias de variáveis por pessoa</Title>
          <TabelaDados mb="sm">
            <thead>
              <tr>
                <Th>Pessoa</Th>
                <Th>Origem</Th>
                <Th num>Média mensal (R$)</Th>
              </tr>
            </thead>
            <tbody>
              {d.mediasResolvidas.map((m) => (
                <tr key={m.funcionarioId}>
                  <td>{m.nome}</td>
                  <td>
                    <Badge variant="light" color={m.fonte === "informada" ? "sillis" : m.fonte === "historico" ? "teal" : "gray"}>
                      {ROTULO_FONTE[m.fonte] ?? m.fonte}
                    </Badge>
                    {m.alerta && (
                      <Tooltip label={m.alerta}>
                        <Text component="span" size="xs" c="orange" ml={6}>sem histórico</Text>
                      </Tooltip>
                    )}
                  </td>
                  <TdNum>
                    {editavel ? (
                      <MoedaField
                        aria-label={`Média de ${m.nome}`}
                        valor={overrides.get(m.funcionarioId) ?? null}
                        onChange={(v) => setOverrides(new Map(overrides).set(m.funcionarioId, v))}
                        size="xs"
                        w={130}
                        styles={{ input: { textAlign: "right" } }}
                      />
                    ) : m.fonte === "informada" && m.valorInformadoCentavos !== null ? (
                      fmtMoeda(m.valorInformadoCentavos)
                    ) : (
                      "—"
                    )}
                  </TdNum>
                </tr>
              ))}
            </tbody>
          </TabelaDados>
          {editavel && (
            <Group justify="flex-end" mb="sm">
              <Button
                size="xs"
                variant="light"
                loading={ocupado}
                onClick={() =>
                  void acao(
                    () =>
                      api.patch(`/processamentos/${d.id}`, {
                        mediasOverrides: [...overrides.entries()].filter(([, v]) => v !== null).map(([funcionarioId, valorCentavos]) => ({ funcionarioId, valorCentavos: valorCentavos! })),
                      }),
                    "Médias salvas.",
                  )
                }
              >
                Salvar médias
              </Button>
            </Group>
          )}
          {d.avisosMedias.length > 0 && (
            <Text size="xs" c="orange" mb="sm">{d.avisosMedias.join(" ")}</Text>
          )}

          {d.ultimaRun && (
            <>
              <Divider my="sm" />
              <Title order={5} mb={4}>Resultado (última execução)</Title>
              <Group gap="xl" mb="sm" wrap="wrap">
                <div>
                  <Text size="xs" c="dimmed">Proventos</Text>
                  <Text fw={700}>{fmtMoeda(d.ultimaRun.resultado.totais.proventosCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Descontos</Text>
                  <Text fw={700}>{fmtMoeda(d.ultimaRun.resultado.totais.descontosCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Líquido da parcela</Text>
                  <Text fw={700} c="sillis.7" size="lg">{fmtMoeda(d.ultimaRun.resultado.totais.liquidoCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">FGTS (8%)</Text>
                  <Text fw={700}>{fmtMoeda(d.ultimaRun.resultado.totais.fgtsCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Elegíveis</Text>
                  <Text fw={700}>
                    {d.ultimaRun.resultado.totais.headcount.empregados} ({d.ultimaRun.resultado.totais.headcount.suspensos} suspensos)
                  </Text>
                </div>
              </Group>
              <TabelaDados>
                <thead>
                  <tr>
                    <Th>Pessoa</Th>
                    <Th num>Avos</Th>
                    <Th num>{d.tipo === "decimo_primeira" ? "1ª parcela" : "2ª parcela"}</Th>
                    <Th num>INSS exclusivo</Th>
                    <Th num>IRRF exclusivo</Th>
                    <Th num>Líquido</Th>
                    <Th num>FGTS</Th>
                  </tr>
                </thead>
                <tbody>
                  {d.ultimaRun.resultado.pessoas.map((p) => {
                    const valor = (cod: number) => p.eventos.find((e) => e.codigoDominio === cod)?.valorCentavos ?? 0;
                    const avos13 = p.memoria?.avos13;
                    return (
                      <Fragment key={p.codigoDominio}>
                        <tr>
                          <td>
                            {p.nome}
                            {p.situacao === "suspenso_inss" && (
                              <Badge ml={6} size="xs" color="yellow">suspensa</Badge>
                            )}
                          </td>
                          <TdNum>{avos13 ? avos13.avos : "—"}</TdNum>
                          <TdNum>{valor(d.tipo === "decimo_primeira" ? 9001 : 9002) > 0 ? fmtMoeda(valor(d.tipo === "decimo_primeira" ? 9001 : 9002)) : "—"}</TdNum>
                          <TdNum>{valor(9005) > 0 ? fmtMoeda(valor(9005)) : "—"}</TdNum>
                          <TdNum>{valor(9006) > 0 ? fmtMoeda(valor(9006)) : "—"}</TdNum>
                          <TdNum>
                            <strong>{fmtMoeda(p.totais.liquidoCentavos)}</strong>
                          </TdNum>
                          <TdNum>{fmtMoeda(p.totais.fgtsCentavos)}</TdNum>
                        </tr>
                        {avos13 && avos13.meses.length > 0 && (
                          <tr>
                            <td colSpan={7}>
                              <Text size="xs" c="dimmed">
                                Avos mês a mês (dias trabalhados; ✓ conta — Lei 4.090/62 §2º):{" "}
                                {avos13.meses.map((m, i) => (
                                  <Tooltip
                                    key={m.mes}
                                    label={`${ROTULO_MES[i]}: ${m.diasTrabalhados} dias — ${ROTULO_MOTIVO[m.motivo] ?? m.motivo}${m.contaAvo ? " (conta avo)" : " (não conta)"}`}
                                  >
                                    <Text
                                      span
                                      size="xs"
                                      c={m.contaAvo ? "green.8" : m.motivo === "suspenso_inss" ? "yellow.9" : m.motivo === "faltas_injustificadas" ? "red.8" : "dimmed"}
                                    >
                                      {ROTULO_MES[i]} {m.diasTrabalhados}
                                      {m.contaAvo ? "✓" : ""}{" "}
                                    </Text>
                                  </Tooltip>
                                ))}
                              </Text>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </TabelaDados>
            </>
          )}
        </>
      )}

      <VisualizadorRelatorio relatorio={relatorio} onFechar={() => setRelatorio(null)} />
      <Confirmacao
        aberta={confirmar !== null}
        titulo={confirmar?.titulo ?? ""}
        consequencia={confirmar?.consequencia ?? ""}
        rotuloConfirmar={confirmar?.rotulo}
        onConfirmar={() => void acao(() => confirmar!.acao(), "Pronto.")}
        onCancelar={() => setConfirmar(null)}
      />
    </Card>
  );
}
